import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import Input from '../../common/Form/Input';
import Radio from '../../common/Form/Radio';
import TextArea from '../../common/Form/TextArea';
import { expenseTypes } from './RaisePoForm';
import { useFormContext } from 'react-hook-form';
import { actualValueForDropdown, OrgTypes } from '../../utils/common';
import Vendors from '../Inputs/Vendors';
import Site from '../Inputs/Site';
import { QuotesCard } from './QuotesCard';
import DatePicker from '../../common/Form/DatePicker';
import { useDispatch, useSelector } from 'react-redux';
import { canManageOtherOrgs } from '../../utils/access';
import Select from '../../common/Form/Select';
import { getApprovalTemplates } from '../../actions/payments';
import { debounce } from '../../utils/common';
import { getOrgList } from '../../actions/org';
import { CLEAR_PO_ORG_LIST_DROPDOWN_LIST_DATA } from '../../types/org';
import { TermsAndCondtion } from './TermsAndConditions';
import { openCustomModal } from '../../actions/modal';
import AddNewVendor from './AddNewVendor';
import { motion } from 'framer-motion';
import { CLEAR_CREATED_VENDOR_FROM_PO } from '../../types/user';
import AddAssetTypes from '../../pages/Modals/Inventory/AddAssetTypes';

export const RaisePoInputs = ({
    quotesArrayName,
    showCustomerDropdown,
    onApprovalRequiredChange,
    editorRef
}) => {
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);
    const approvalTemplatesData = useSelector((state) => state.payments.approvalTemplatesData);
    const createdVendorFromPo = useSelector((state) => state.user.createdVendorFromPo);
    const isGetApprovalTemplateLoading = useSelector((state) => state.loading.isGetApprovalTemplateLoading);
    const { watch, setValue } = useFormContext();
    const dispatch = useDispatch();

    useEffect(() => {
        const findGST = newLoggedUserData.orgVerificationDocs.find((elem) => elem.ref === 'org-verification-gst');
        if (findGST && !showCustomerDropdown) setValue('org_gst_number', findGST.value);
        if (newLoggedUserData.org?.address?.plain_text && !showCustomerDropdown) {
            setValue('org_address', newLoggedUserData.org?.address?.plain_text);
            setValue('shipping_address', newLoggedUserData.org?.address?.plain_text);
            setValue('billing_address', newLoggedUserData.org?.address?.plain_text);
        }
        if (!showCustomerDropdown) {
            setValue('customer', newLoggedUserData.org?.id)
        }
        setValue('approval_required', { key: 'auto', value: 'auto', label: 'Auto' });
    }, [newLoggedUserData, showCustomerDropdown, setValue]);


    useEffect(() => {
        let customer = watch('customer');
        if (!showCustomerDropdown) {
            customer = newLoggedUserData.org?.id;
        }
        let expense_type = watch('expense_type');
        if (customer && expense_type) {
            const org_id = actualValueForDropdown(customer);
            const expense_type = watch('expense_type');
            setValue('approval_required', null);
            dispatch(getApprovalTemplates({ org_id, module: "PURCHASE_ORDER", category: expense_type }));
        }
    }, [dispatch, watch('customer'), watch('expense_type'), newLoggedUserData.org?.id]);

    const memoizedApprovalRequiredOptions = useMemo(() => {
        const org_id = actualValueForDropdown(watch('customer'));
        if (org_id && approvalTemplatesData && approvalTemplatesData[org_id] && approvalTemplatesData[org_id].length > 0) {
            return [{ label: 'Auto', value: 'auto', key: 'auto' }, ...approvalTemplatesData[org_id].map((template) => ({ label: template.name, value: template.id, key: template.id, levels: template.levels })), { label: 'Custom', value: 'custom', key: 'custom' }];
        }
        return [{ label: 'Auto', value: 'auto', key: 'auto' }, { label: 'Custom', value: 'custom', key: 'custom' }];
    }, [approvalTemplatesData, watch('customer')]);


    const onAddNewVendorClick = () => {
        const modalWidth = '60vw'
        const modalHeight = "auto"
        dispatch(openCustomModal({
            heading: 'Add New Vendor',
            modalComponent: <AddNewVendor org_id={actualValueForDropdown(watch('customer'))} />,
            modalWidth,
            modalHeight,
        }));
    }

    const memoizedCreatedVendorFromPo = useMemo(() => {
        if (createdVendorFromPo) {
            dispatch({ type: CLEAR_CREATED_VENDOR_FROM_PO });
            return createdVendorFromPo;
        }
        return null;
    }, [createdVendorFromPo, dispatch]);


    let org_id = actualValueForDropdown(watch('customer'));
    if (!showCustomerDropdown) {
        org_id = newLoggedUserData.org?.id;
    }

    return (
        <>
            <div className='px-5 py-4 flex flex-col gap-6'>
                <div className='border border-scogof0 rounded-lg shadow-md px-6 pb-5 pt-3 bg-white hover:shadow-lg transition-shadow duration-300'>
                    <h3 className='text-xl font-semibold mb-4 border-b border-scogof0 pb-2 text-scogo15'>Customer Details</h3>
                    <div className='grid grid-cols-5 gap-4'>
                        {showCustomerDropdown && <RenderCustomerDropdowm />}
                        <Input required label='Customer GST' name='org_gst_number' pattern={/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/} className='focus:border-scogo15' />
                        <Input label='Customer Address' name='org_address' />
                        <Input label='Shipping Address' name='shipping_address' />
                        <Input label='Billing Address' name='billing_address' />
                    </div>
                </div>

                {/* Vendor Details Card */}
                <div className='border border-scogof0 rounded-lg shadow-md px-6 pb-5 pt-3 bg-white hover:shadow-lg transition-shadow duration-300'>
                    <div className='flex justify-between items-center mb-4 border-b border-scogof0 pb-2'>
                        <h3 className='text-xl font-semibold text-scogo15'>Vendor Details</h3>
                        {actualValueForDropdown(watch('customer')) ? <motion.button
                            type="button"
                            className="flex items-center gap-1 px-3 py-2 bg-orange-100 text-orange-700 rounded-md hover:bg-orange-200 transition-colors"
                            onClick={onAddNewVendorClick}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <span className="material-icons-outlined text-font11">inventory</span>
                            <span>Add New Vendor</span>
                        </motion.button> : null}
                    </div>
                    <div className='grid grid-cols-3 gap-4'>
                        <Vendors onChange={
                            (_, event) => {
                                if (event && event.verifications) {
                                    setValue('vendor_gst_number', event?.verifications?.find((elem) => elem.ref === 'org-verification-gst')?.value);
                                    setValue('vendor_address', event?.address?.plain_text);
                                } else {
                                    setValue('vendor_gst_number', null);
                                    setValue('vendor_address', null);
                                }
                            }
                        } get_verification_data={true} prefillObj={memoizedCreatedVendorFromPo} label='For Vendor' name='vendor' required className='focus:border-scogo15' vendors_from={actualValueForDropdown(watch('customer'))} />
                        <Input label='Vendor GST' name='vendor_gst_number' pattern={/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/} className='focus:border-scogo15' />
                        <Input label='Vendor Address' name='vendor_address' className='focus:border-scogo15' />
                    </div>
                </div>

                {/* PO Details Card */}
                <div className='border border-scogof0 rounded-lg shadow-md px-6 pb-5 pt-3 bg-white hover:shadow-lg transition-shadow duration-300'>
                    <div className='flex justify-between items-center mb-4 border-b border-scogof0 pb-2'>
                        <h3 className='text-xl font-semibold mb-4 border-b border-scogof0 pb-2 text-scogo15'>PO Details</h3>
                        {org_id ? <motion.button
                            type="button"
                            className="flex items-center gap-1 px-3 py-2 bg-orange-100 text-orange-700 rounded-md hover:bg-orange-200 transition-colors"
                            onClick={() => {
                                dispatch(
                                    openCustomModal({
                                        heading: 'Add Asset Type',
                                        modalWidth: '50rem',
                                        modalHeight: 'auto',
                                        modalComponent: <AddAssetTypes org_id={org_id} type='assets' />,
                                    })
                                )
                            }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <span className="material-icons-outlined text-font11">inventory</span>
                            <span>Add New Asset Type</span>
                        </motion.button> : null}
                    </div>
                    <div className='grid grid-cols-4 gap-4'>
                        <Radio options={expenseTypes} label='Expense Type' name='expense_type' className='flex gap-3 items-center' />
                        <Select isLoading={isGetApprovalTemplateLoading} onChange={onApprovalRequiredChange} required options={memoizedApprovalRequiredOptions} label='Approval Required' name='approval_required' className='w-full focus:border-scogo15' />
                        <Input required label='Title' name='title' className='focus:border-scogo15' />
                        <TextArea label='Description' name='description' required className='focus:border-scogo15 resize-none' />
                        <Input label='Payment Terms in Days' name='payment_terms' type='number' className='focus:border-scogo15' />
                        <Input label='External PR Number' name='external_pr_number' className='focus:border-scogo15' />
                        <Input label='External PO Number' name='external_po_number' className='focus:border-scogo15' />
                        <Site
                            label='Site'
                            name='location_id'
                            org_id={actualValueForDropdown(watch('customer'))}
                            className='focus:border-scogo15'
                        />
                        <Input name='emails' label='Send Updates to Users (use , for multiple emails)' className='focus:border-scogo15' />
                        {/* <DatePicker label='Quotation Date' name='quote_date' className='w-full focus:border-scogo15' /> */}
                        <DatePicker label='PO Date' name='po_date' className='w-full focus:border-scogo15' />
                    </div>
                </div>
            </div>

            {/* Quote Card */}
            <div className='px-5 py-4'>
                <QuotesCard
                    quotesArrayName={quotesArrayName}
                    showCustomerDropdown={showCustomerDropdown}
                />
            </div>
            <div className='px-5 py-4'>
                <h3 className='text-xl font-semibold mb-4 border-b border-scogof0 pb-2 text-scogo15'>Terms and Conditions</h3>
                <TermsAndCondtion editorRef={editorRef} org_id={showCustomerDropdown ? actualValueForDropdown(watch('customer')) : newLoggedUserData.org?.id} currentValue={watch('terms_and_conditions')} />
            </div>
        </>
    );
};




const RenderCustomerDropdowm = ({ }) => {
    const { setValue } = useFormContext();
    const poOrgListDropdown = useSelector((state) => state.org.poOrgListDropdown);
    const isPoOrgListLoading = useSelector((state) => state.loading.isPoOrgListLoading);

    const dispatch = useDispatch();

    const getOrganisationList = ({ limit = 100, page = 1, query = '' }) => {
        dispatch(getOrgList({ limit, page, query, dropdown: true, type: OrgTypes.CUSTOMER, get_verification_data: true, source: 'po' }));
    };

    const debouncedOrgSearchSearch = useCallback(debounce(getOrganisationList), []);

    useEffect(() => {
        getOrganisationList({ limit: 100, page: 1, query: '', get_verification_data: true });
        return () => {
            dispatch({ type: CLEAR_PO_ORG_LIST_DROPDOWN_LIST_DATA });
        }
    }, [dispatch]);

    return (
        <Select
            className='w-full focus:border-scogo15'
            label='Customer'
            name='customer'
            required={true}
            options={poOrgListDropdown}
            isLoading={isPoOrgListLoading}
            onInputChange={(event) => {
                if (event.length > 0) {
                    debouncedOrgSearchSearch({ query: event })
                }
            }}
            onChange={(event) => {
                const orgId = actualValueForDropdown(event);
                if (!orgId) return;

                const findGST = event?.verifications?.find((elem) => elem.ref === 'org-verification-gst');
                if (findGST) setValue('org_gst_number', findGST.value);
                else setValue('org_gst_number', null);

                if (event?.address?.plain_text) {
                    setValue('org_address', event?.address?.plain_text);
                    setValue('shipping_address', event?.address?.plain_text);
                    setValue('billing_address', event?.address?.plain_text);
                } else {
                    setValue('org_address', null);
                    setValue('shipping_address', null);
                    setValue('billing_address', null);
                }
            }}
            readOnly={false}
            preSelectedIfSingleOption={true}
            isMulti={false}
        />
    )
}